import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  AlertTitle,
  LinearProgress,
  Tooltip,
  Divider,
  Paper,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon,
  Rule as RuleIcon,
} from "@mui/icons-material";
import { ConversationValidationResults, ValidationResult } from "../utils/validationEngine";
import { ValidationOperator } from "../API";

interface ValidationResultsProps {
  results: ConversationValidationResults | null;
  loading?: boolean;
  error?: string;
  title?: string;
  showConversationInfo?: boolean;
}

export const ValidationResults: React.FC<ValidationResultsProps> = ({
  results,
  loading = false,
  error = null,
  title = "Validation Results",
  showConversationInfo = true,
}) => {
  const theme = useTheme();

  console.log("📊 ValidationResults component rendered with:");
  console.log("📊 results:", results);
  console.log("📊 loading:", loading);
  console.log("📊 error:", error);
  console.log("📊 title:", title);
  console.log("📊 showConversationInfo:", showConversationInfo);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Running validation checks...
            </Typography>
            <LinearProgress sx={{ mt: 1 }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Alert severity="error">
            <AlertTitle>Validation Error</AlertTitle>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!results) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Alert severity="info">
            <AlertTitle>No Results Available</AlertTitle>
            No validation results to display. Run a validation to see results here.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const getStatusColor = () => {
    if (results.passedRules === results.totalRules) return "success";
    if (results.passedRules === 0) return "error";
    return "warning";
  };

  const getStatusIcon = () => {
    if (results.passedRules === results.totalRules) {
      return <CheckCircleIcon color="success" />;
    }
    if (results.passedRules === 0) {
      return <ErrorIcon color="error" />;
    }
    return <WarningIcon color="warning" />;
  };

  const getStatusText = () => {
    if (results.passedRules === results.totalRules) return "All Passed";
    if (results.passedRules === 0) return "All Failed";
    return "Partially Passed";
  };

  const getOperatorDescription = (operator: ValidationOperator) => {
    switch (operator) {
      case ValidationOperator.SHOULD_CONTAIN:
        return "Text Content Check";
      case ValidationOperator.SHOULD_NOT_CONTAIN:
        return "Text Exclusion Check";
      case ValidationOperator.SHOULD_RUN_TOOL:
        return "Tool Usage Check";
      case ValidationOperator.SHOULD_NOT_RUN_TOOL:
        return "Tool Restriction Check";
      case ValidationOperator.CUSTOM_LLM_JUDGE:
        return "Custom LLM Evaluation";
      default:
        return "Unknown Check";
    }
  };

  const getOperatorIcon = (operator: ValidationOperator) => {
    switch (operator) {
      case ValidationOperator.SHOULD_CONTAIN:
      case ValidationOperator.SHOULD_NOT_CONTAIN:
        return <AssignmentIcon fontSize="small" />;
      case ValidationOperator.SHOULD_RUN_TOOL:
      case ValidationOperator.SHOULD_NOT_RUN_TOOL:
        return <RuleIcon fontSize="small" />;
      case ValidationOperator.CUSTOM_LLM_JUDGE:
        return <AssignmentIcon fontSize="small" />;
      default:
        return <RuleIcon fontSize="small" />;
    }
  };

  const successRate = results.totalRules > 0 ? (results.passedRules / results.totalRules) * 100 : 0;

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>

          {/* Overall Status Summary */}
          <Paper
            elevation={1}
            sx={{
              p: 2,
              bgcolor: theme.palette.mode === 'dark' 
                ? theme.palette.background.default 
                : theme.palette.grey[50],
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                {getStatusIcon()}
                <Typography variant="h6">
                  {getStatusText()}
                </Typography>
              </Box>
              <Chip
                label={`${successRate.toFixed(1)}%`}
                color={getStatusColor()}
                variant="outlined"
              />
            </Box>

            <Box display="flex" gap={2} mb={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Rules
                </Typography>
                <Typography variant="h6">
                  {results.totalRules}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Passed
                </Typography>
                <Typography variant="h6" color="success.main">
                  {results.passedRules}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Failed
                </Typography>
                <Typography variant="h6" color="error.main">
                  {results.failedRules}
                </Typography>
              </Box>
            </Box>

            {/* Progress Bar */}
            <Box sx={{ mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={successRate}
                color={getStatusColor()}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {results.passedRules} of {results.totalRules} validations passed
            </Typography>
          </Paper>

          {/* Conversation Info */}
          {showConversationInfo && (
            <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Chip
                icon={<AccessTimeIcon />}
                label={`Executed: ${results.executedAt.toLocaleString()}`}
                variant="outlined"
                size="small"
              />
              <Chip
                label={`Agent ID: ${results.agentId}`}
                variant="outlined"
                size="small"
              />
              <Chip
                label={`Conversation ID: ${results.conversationId}`}
                variant="outlined"
                size="small"
              />
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Individual Validation Results */}
        <Typography variant="h6" gutterBottom>
          Detailed Results
        </Typography>

        <List disablePadding>
          {results.results.map((result: ValidationResult, index: number) => (
            <React.Fragment key={result.ruleId}>
              <Accordion
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  mb: 1,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: result.passed 
                      ? theme.palette.success.light + '20'
                      : theme.palette.error.light + '20',
                    borderRadius: "4px 4px 0 0",
                  }}
                >
                  <Box display="flex" alignItems="center" width="100%">
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {result.passed ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </ListItemIcon>
                    <Box flex={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {result.ruleName && result.ruleName.trim() !== "" 
                          ? result.ruleName 
                          : result.expectedValue || getOperatorDescription(result.operator)
                        }
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Tooltip title={getOperatorDescription(result.operator)}>
                          <Chip
                            icon={getOperatorIcon(result.operator)}
                            label={result.operator}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                        <Chip
                          label={result.passed ? "PASSED" : "FAILED"}
                          size="small"
                          color={result.passed ? "success" : "error"}
                        />
                      </Box>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Box>
                    {/* Validation Message */}
                    <Alert 
                      severity={result.passed ? "success" : "error"}
                      sx={{ mb: 2 }}
                    >
                      {result.message}
                    </Alert>

                    {/* Expected vs Actual Values */}
                    {result.expectedValue && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          Expected Value:
                        </Typography>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1,
                            bgcolor: theme.palette.background.default,
                            border: `1px solid ${theme.palette.divider}`,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                          }}
                        >
                          {result.expectedValue}
                        </Paper>
                      </Box>
                    )}

                    {result.actualValue && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          Actual Value:
                        </Typography>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1,
                            bgcolor: theme.palette.background.default,
                            border: `1px solid ${theme.palette.divider}`,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            maxHeight: 200,
                            overflow: 'auto',
                          }}
                        >
                          {result.actualValue}
                        </Paper>
                      </Box>
                    )}

                    {/* Error Details */}
                    {result.error && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        <AlertTitle>Error Details</AlertTitle>
                        {result.error}
                      </Alert>
                    )}

                    {/* Rule Metadata */}
                    <Box sx={{ mt: 2, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                      <Typography variant="caption" color="text.secondary">
                        Rule ID: {result.ruleId} | Operator: {result.operator}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </React.Fragment>
          ))}
        </List>

        {/* Empty State */}
        {results.results.length === 0 && (
          <Alert severity="info">
            <AlertTitle>No Validation Rules</AlertTitle>
            No validation rules were executed. Ensure the agent has active validation rules configured.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default ValidationResults;
